import { Field as ChakraField, Fieldset, HStack, Input, RadioGroup, Text, VStack } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import CustomInputDateTime from '@components/common/inputs/CustomInputDateTime';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { Switch } from '@components/ui/switch';
import { Field, FormikProps, FormikProvider } from 'formik';
import { FormattedMessage } from 'react-intl';

import { User } from '@/types/authent/login.types';
import { DateTimeEnum } from '@/types/dateTime.types';

type Props = {
  formik: FormikProps<Partial<User>>;
};

export default function UserForm({ formik }: Readonly<Props>) {
  return (
    <CockpitFormLayout title="components.usersManagement.users.userParams.title">
      <FormikProvider value={formik}>
        <Fieldset.Root
          invalid={Object.keys(formik.errors).some((key) => formik.touched[key as keyof typeof formik.touched])}
        >
          <VStack gap={5}>
            <ChakraField.Root
              readOnly={formik.initialValues.id !== undefined}
              variant="cockpit"
              required
              invalid={formik.touched.login && !!formik.errors.login}
            >
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="authent.user.login" />
                <RequiredIndicator />
                {formik.initialValues.login !== formik.values.login && <ModifiedTag />}
              </ChakraField.Label>
              <Field as={Input} type="text" name="login" id="login" />
            </ChakraField.Root>
            <ChakraField.Root
              required
              variant="cockpit"
              invalid={formik.touched.firstName && !!formik.errors.firstName}
            >
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="authent.user.firstName" />
                <RequiredIndicator />
                {formik.initialValues.firstName !== formik.values.firstName && <ModifiedTag />}
              </ChakraField.Label>
              <Field as={Input} type="text" name="firstName" id="firstName" />
            </ChakraField.Root>
            <ChakraField.Root required variant="cockpit" invalid={formik.touched.name && !!formik.errors.name}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="authent.user.name" />
                <RequiredIndicator />
                {formik.initialValues.name !== formik.values.name && <ModifiedTag />}
              </ChakraField.Label>
              <Field as={Input} type="text" name="name" id="name" />
            </ChakraField.Root>
            <ChakraField.Root required variant="cockpit" invalid={formik.touched.email && !!formik.errors.email}>
              <ChakraField.Label width="100%" height="20px">
                <FormattedMessage id="authent.user.email" />
                <RequiredIndicator />
                {formik.initialValues.email !== formik.values.email && <ModifiedTag />}
              </ChakraField.Label>
              <Field as={Input} type="text" name="email" id="email" />
            </ChakraField.Root>
            <VStack padding={4} gap={3} width="100%" border="2px solid" borderColor="neutral.850">
              <HStack width="100%" marginTop={1}>
                <Text>
                  <FormattedMessage id="components.usersManagement.users.userParams.activate" />
                </Text>
                <Field
                  as={Switch}
                  size="sm"
                  marginLeft="auto"
                  checked={formik.values.active}
                  name="active"
                  id="active"
                />
              </HStack>
              {formik.values.active && (
                <>
                  <RadioGroup.Root
                    value={formik.values.deactivationDate === null ? 'noExpirationDate' : 'expirationDate'}
                    onValueChange={({ value }) => {
                      if (value === 'expirationDate') {
                        void formik.setFieldValue(
                          'deactivationDate',
                          new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                        );
                      } else {
                        void formik.setFieldValue('deactivationDate', null);
                      }
                    }}
                  >
                    <RadioGroup.Item value="noExpirationDate">
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemText>
                        <FormattedMessage id="components.usersManagement.users.userParams.noExpirationDate" />
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                    <RadioGroup.Item value="expirationDate">
                      <RadioGroup.ItemIndicator />
                      <RadioGroup.ItemHiddenInput />
                      <RadioGroup.ItemText>
                        <FormattedMessage id="components.usersManagement.users.userParams.expirationDate" />
                      </RadioGroup.ItemText>
                    </RadioGroup.Item>
                  </RadioGroup.Root>
                  {formik.values.deactivationDate && (
                    <CustomInputDateTime
                      type={DateTimeEnum.DATE}
                      value={formik.values.deactivationDate}
                      onChange={(date) => formik.setFieldValue('deactivationDate', date)}
                    />
                  )}
                </>
              )}
            </VStack>
          </VStack>
        </Fieldset.Root>
      </FormikProvider>
    </CockpitFormLayout>
  );
}
